import React from 'react';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

export default function Footer() {
  return (
    <footer className="border-t border-slate-mist/40 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <AppLogo text="Lenders Home" iconName="BanknotesIcon" size={28} className="text-slate mb-3" />
            <p className="text-xs text-slate-fog font-body leading-relaxed">
              Lenders Home LLC — connecting borrowers with responsible lending solutions.
            </p>
          </div>
          {/* Company */}
          <div>
            <div className="text-xs font-semibold text-slate font-body uppercase tracking-wide mb-3">Company</div>
            <div className="space-y-2">
              {[
                { label: 'About Us', href: '/about' },
                { label: 'How It Works', href: '/how-it-works' },
                { label: 'Loan Guide', href: '/loan-guide' },
                { label: 'Contact Us', href: '/contact' },
              ].map((link) => (
                <Link key={link.label} href={link.href} className="block text-sm text-slate-fog hover:text-slate transition-colors font-body">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
          {/* Support */}
          <div>
            <div className="text-xs font-semibold text-slate font-body uppercase tracking-wide mb-3">Support</div>
            <div className="space-y-2">
              {[
                { label: 'FAQ', href: '/faq' },
                { label: 'Sign In', href: '/login' },
                { label: 'My Dashboard', href: '/dashboard' },
                { label: 'My Applications', href: '/loans' },
              ].map((link) => (
                <Link key={link.label} href={link.href} className="block text-sm text-slate-fog hover:text-slate transition-colors font-body">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
          {/* Legal */}
          <div>
            <div className="text-xs font-semibold text-slate font-body uppercase tracking-wide mb-3">Legal</div>
            <div className="space-y-2">
              {[
                { label: 'Privacy Policy', href: '/privacy' },
                { label: 'Terms & Conditions', href: '/terms' },
                { label: 'Disclaimer', href: '/disclaimer' },
              ].map((link) => (
                <Link key={link.label} href={link.href} className="block text-sm text-slate-fog hover:text-slate transition-colors font-body">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Trustpilot Rating */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 py-5 mb-2 border-t border-slate-mist/30">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate font-body">Excellent</span>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg key={star} className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="2" fill="#00B67A" />
                  <path d="M12 4.5L13.545 9.545H18.82L14.637 12.59L16.182 17.636L12 14.59L7.818 17.636L9.363 12.59L5.18 9.545H10.455L12 4.5Z" fill="white" />
                </svg>
              ))}
            </div>
            <span className="text-sm text-slate-fog font-body">5.0 out of 5</span>
          </div>
          <div className="flex items-center gap-1.5">
            <svg className="w-16 h-5" viewBox="0 0 120 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <text x="0" y="18" fontFamily="Arial, sans-serif" fontSize="16" fontWeight="bold" fill="#00B67A">Trustpilot</text>
            </svg>
            <span className="text-xs text-slate-fog font-body">Based on verified reviews</span>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-slate-mist/30">
          <div className="flex items-center gap-3">
            {[
              { name: 'facebook', icon: 'GlobeAltIcon' },
              { name: 'twitter', icon: 'ChatBubbleLeftRightIcon' },
            ].map((s) => (
              <a
                key={s.name}
                href="#"
                className="w-8 h-8 rounded-full bg-cloud flex items-center justify-center text-slate-fog hover:text-sky hover:bg-sky-light transition-all"
                aria-label={s.name}
              >
                <Icon name={s.icon as any} size={15} />
              </a>
            ))}
          </div>
          <span className="text-sm text-slate-fog font-body">© 2026 Lenders Home LLC. All rights reserved.</span>
        </div>

        {/* Disclaimer */}
        <p className="mt-4 pt-4 border-t border-slate-mist/30 text-xs text-slate-fog font-body leading-relaxed max-w-4xl">
          Lenders Home LLC is not a direct lender. We connect borrowers with eligible lending programs. Loan approval is not guaranteed. All loan terms are subject to lender approval and applicable eligibility requirements. Rates and terms vary by applicant profile and program. This platform does not provide financial, legal, or tax advice. NMLS information available upon request.
        </p>
      </div>
    </footer>
  );
}